import React, { type FC } from 'react'
import { Table, TableCell, TableContainer, TableHead, TableRow, Typography, Checkbox, TableBody } from '@mui/material'
import Switch, { type SwitchProps } from '@mui/material/Switch'
import { styled } from '@mui/material/styles'
import { useSelector } from 'react-redux'
import EmptyTable from './EmptyTable'
import AddMoreDetails from '../configureDevice'
import { GenXDropdown, type Option } from '../../../../components/GenXDropdown'
import { type RootState } from '../../../../store'
import { type FrequencyOption } from '../../services/frequencyMetaDataService'
import { type Parameter } from '../interface'

// Default fallback options (for backward compatibility)
const defaultPollingIntervalOptions = [
  { label: '10 Seconds', value: '10' },
  { label: '30 Seconds', value: '30' },
  { label: 'Default (1 Minute)', value: '60' }
]

/**
 * Helper function to convert seconds value to display option format
 * @param seconds - The seconds value to convert
 * @param options - Available frequency options from API
 * @returns Corresponding display option or default fallback
 */
const convertSecondsToDisplayValueOption = (seconds: number | null | string, options: FrequencyOption[]): Option => {
  if (seconds == null || seconds === '') {
    const defaultOption = options.find(opt => opt.label.includes('Default') || opt.label.includes('1 Minute'))
    return defaultOption !== undefined ? { label: defaultOption.label, value: defaultOption.value } : { label: 'Default (1 Minute)', value: '60' }
  }

  const numSeconds = typeof seconds === 'string' ? parseInt(seconds) : seconds
  const matchingOption = options.find(opt => parseInt(opt.value) === numSeconds)

  if (matchingOption !== undefined) {
    return { label: matchingOption.label, value: matchingOption.value }
  }

  // Fallback to default if no match found
  const defaultOption = options.find(opt => opt.label.includes('Default') || opt.label.includes('1 Minute'))
  return defaultOption !== undefined ? { label: defaultOption.label, value: defaultOption.value } : { label: 'Default (1 Minute)', value: '60' }
}

/**
 * Helper function to convert display value back to seconds
 * @param displayValue - The display value string to parse
 * @returns Numeric seconds value
 */
const convertDisplayValueToSeconds = (displayValue: string): number => {
  // Extract numeric value from the display value
  const numericValue = parseInt(displayValue)
  return isNaN(numericValue) ? 60 : numericValue // Default to 60 seconds if parsing fails
}

const styles = {
  selectAllCheckbox: {
    color: '#909090'
  },
  checkbox: {
    color: '#909090'
  },
  table: {
    backgroundColor: '#272727',
    borderBottom: 'none',
    boxShadow: 'none !important'
  },
  tableHead: {
    backgroundColor: '#303030',
    border: '1px solid #101010',
    boxShadow: '1px 1px 1px 0px rgba(0, 0, 0, 0.15)',
    borderRadius: '10px 10px 0px 0px'
  },
  tableHeadCell: {
    backgroundColor: '#303030',
    borderBottom: 'none',
    padding: '5px'
  },
  tableCell: {
    backgroundColor: '#272727',
    borderBottom: 'none',
    padding: '5px'
  },
  tableHeaderTypography: {
    color: '#F0F0F0',
    fontFeatureSettings: "'clig' off, 'liga' off",
    fontFamily: 'Honeywell Sans Web',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 800,
    lineHeight: '24px'
  },
  tableCellTypography: {
    color: '#F0F0F0',
    fontFeatureSettings: "'clig' off, 'liga' off",
    fontFamily: 'Honeywell Sans Web',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '24px'
  },
  tableContainer: {
    height: '430px',
    maxHeight: '430px',
    overflow: 'auto'
  }
}
export const CustomSwitch = styled((props: SwitchProps) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 48,
  height: 26,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: '5px 9px',
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: theme.palette.mode === 'dark' ? '#B0B0B0' : '#64C3FF',
        opacity: 1,
        border: 0
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5
      },
      '& .MuiSwitch-thumb': {
        color: '#000'
      }
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#33cf4d',
      border: '6px solid #fff'
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: '#707070'
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: theme.palette.mode === 'light' ? 0.7 : 0.3
    }
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 15,
    height: 15
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: theme.palette.mode === 'light' ? '#B0B0B0' : '#64C3FF',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500
    })
  }
}))

interface ParametersTableProps {
  tableHeaders: string[]
  parameters: Parameter[] | []
  searchText: string
  selectedModbusCount: number
  toggleSelectAll: () => void
  toggleSelectIndicator: (id: number) => void
  handleChangeTextBox: (id: string | number, propertyName: string, value: string | number) => void
  handleAlarmSelect: (id: string | number, propertyName: string, value: string) => void
  handleCancel: (id: any,) => void
}

const ParametersTable: FC<ParametersTableProps> = ({ searchText, tableHeaders, parameters, selectedModbusCount, toggleSelectAll, handleAlarmSelect, toggleSelectIndicator, handleChangeTextBox, handleCancel }) => {
  // Get frequency options from Redux store
  const frequencyMetaDataState = useSelector((state: RootState) => state.frequencyMetaDataState)
  const pollingIntervalOptions = frequencyMetaDataState.data?.modbusFrequencies ?? defaultPollingIntervalOptions

  const filteredParameters = searchText !== ''
    ? parameters.filter(parameter => parameter.parameterName?.toLowerCase().includes(searchText.toLowerCase()) || parameter.registerAddress?.toString().includes(searchText))
    : parameters

  return (
    <TableContainer className="modbus-table" sx={styles.tableContainer}>
      <Table sx={styles.table} stickyHeader >
        <TableHead sx={styles.tableHead} >
          <TableRow sx={{ zIndex: 3, position: 'sticky' }}>
            <TableCell sx={styles.tableHeadCell}>
              <Checkbox
                sx={styles.selectAllCheckbox}
                data-testid="select-all-checkbox"
                className="select-all-checkbox"
                indeterminate={selectedModbusCount > 0 && selectedModbusCount < filteredParameters.length}
                checked={filteredParameters.length > 0 && selectedModbusCount === filteredParameters.length}
                onChange={toggleSelectAll}
                inputProps={{
                  'aria-label': 'Select all'
                }}
              />
            </TableCell>
            {tableHeaders.map((header) => (
              <TableCell key={header} sx={styles.tableHeadCell}>
                <Typography sx={styles.tableHeaderTypography}>
                  {header}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        {filteredParameters.length === 0
          ? (<EmptyTable />)
          : (
          <TableBody className="modbus-table-body">
            {filteredParameters.map((indicator, index) => (
              <TableRow key={indicator.registerAddress}>
                <TableCell sx={styles.tableCell}>
                  <Checkbox
                    color="primary"
                    checked={indicator.isRegisterEnabled}
                    onChange={() => {
                      toggleSelectIndicator(indicator.registerAddress)
                    }}
                    data-testid={`select-indicator-${indicator.registerAddress}`}
                    sx={styles.checkbox}
                  />
                </TableCell>
                <TableCell sx={styles.tableCell}>
                  <Typography sx={styles.tableCellTypography}>
                    {indicator.registerAddress}
                  </Typography>
                </TableCell>
                <TableCell sx={{ ...styles.tableCell, minWidth: '250px' }}>
                  <Typography sx={styles.tableCellTypography}>
                    {indicator.parameterName}
                  </Typography>
                </TableCell>
                <TableCell sx={{ ...styles.tableCell, minWidth: '195px', padding: 1.2 }}>
                      <GenXDropdown
                        menuPlacement={(index > filteredParameters.length - 3 && filteredParameters.length > 2) ? 'top' : 'auto'}
                        options={pollingIntervalOptions}
                        value={convertSecondsToDisplayValueOption(indicator.pollingIntervalMinutes, pollingIntervalOptions)}
                        onSelect={(selectedOption: any) => {
                          const seconds = convertDisplayValueToSeconds(selectedOption.value)
                          handleChangeTextBox(indicator.registerAddress, 'pollingIntervalMinutes', seconds)
                        }}
                        placeholder="Select Frequency"
                        width={'100%'}
                        height={150}
                        disabled={!indicator.isRegisterEnabled || frequencyMetaDataState.isLoading}
                        inputId={`pooling-interval-${indicator.registerAddress}`}
                        isSearchable={false}
                      />
                </TableCell>
                <TableCell sx={{ ...styles.tableCell, minWidth: '160px' }}>
                  <AddMoreDetails isEdit={true} isAlarmEnabledCompo={!indicator.isRegisterEnabled} deviceIndicators={indicator} handleChangeTextBox={handleChangeTextBox} alarmSelect={handleAlarmSelect} handleCancel={handleCancel} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
            )}
      </Table>
    </TableContainer>
  )
}

export default ParametersTable
